import React, { useMemo } from 'react';
import clsx from 'clsx';
import {
  createCalendar,
  DateValue,
  toCalendarDate,
} from '@internationalized/date';
import {
  AriaRangeCalendarProps,
  useRangeCalendar,
} from 'react-aria/useRangeCalendar';
import { filterDOMProps } from 'react-aria/filterDOMProps';
import { mergeProps } from 'react-aria/mergeProps';
import { useObjectRef } from 'react-aria/useObjectRef';

import { useLocale } from 'react-aria/I18nProvider';
import { useDateFormatter } from 'react-aria/useDateFormatter';

import styles from '@danske/sapphire-css/components/calendar/calendar.module.css';

import { Button } from '../../Button';
import { CalendarDaysGrid } from './CalendarDaysGrid';
import { CalendarHeader } from './CalendarHeader';

import {
  useRangeCalendarState,
  type RangeCalendarStateOptions,
  type RangeCalendarState,
} from './useRangeCalendarState';
import { sentenceCase } from './utils';
import {
  SapphireStyleProps,
  useSapphireStyleProps,
  useThemeCheck,
} from '../../utils';
import {
  BaseComponentKeys,
  GlobalDomAttributes,
  InputBaseKeys,
  ValueBasePropsKeys,
} from '../../utils/types';
import { RangeValue } from '@react-types/shared';

export interface PredefinedDateRange {
  label: string;
  dateRange: { start: DateValue; end: DateValue };
}

export interface RangeCalendarProps<
  T extends DateValue = DateValue,
  P extends string = string
> extends SapphireStyleProps,
    GlobalDomAttributes,
    Pick<
      AriaRangeCalendarProps<T>,
      | 'autoFocus'
      | 'focusedValue'
      | 'onFocusChange'
      | 'minValue'
      | 'maxValue'
      | 'isDateUnavailable'
      | InputBaseKeys
      | ValueBasePropsKeys
      | 'autoFocus'
      | BaseComponentKeys
    > {
  /**
   * This callback also gives you the information if the selected range was a
   * predefined date according to the `predefinedRanges` prop.
   */
  onChange?: RangeCalendarStateOptions<DateValue, P>['onChange'];
  /**
   * Whether week numbers are visible as a first column
   */
  showWeekNumbers?: boolean;

  /**
   * Whether to show two months at the same time.
   */
  showTwoMonths?: boolean;

  /**
   * A set of predefined date ranges the user can more conveniently select.
   */
  predefinedRanges?: Record<P, PredefinedDateRange>;

  /**
   * Whether range is allowed to span unavailable dates.
   */
  allowUnavailableDatesWithinRange?: boolean;

  /**
   * If the calendar should always show a fixed number of rows regardless of
   * the current month's number of weeks. This helps preserve the calendar
   * height as the user scrolls through months.
   * @default false
   */
  showFixedWeeks?: boolean;

  /**
   * @default 'lg'
   */
  size?: 'md' | 'lg';
}

const _RangeCalendar = function RangeCalendar<
  T extends DateValue = DateValue,
  P extends string = string
>(
  {
    showWeekNumbers,
    showTwoMonths = false,
    allowUnavailableDatesWithinRange = false,
    showFixedWeeks = false,
    predefinedRanges,
    size = 'lg',
    ...props
  }: RangeCalendarProps<T, P>,
  forwardedRef: React.ForwardedRef<HTMLDivElement>
): JSX.Element {
  useThemeCheck();
  const { styleProps } = useSapphireStyleProps(props);

  const ref = useObjectRef(forwardedRef);
  const { locale } = useLocale();
  const state = useRangeCalendarState({
    ...props,
    locale,
    allowsNonContiguousRanges: allowUnavailableDatesWithinRange,
    visibleDuration: useMemo(
      () => ({ months: showTwoMonths ? 2 : 1 }),
      [showTwoMonths]
    ),
    createCalendar,
  }) as RangeCalendarState<P, T>;

  const { calendarProps, prevButtonProps, nextButtonProps } = useRangeCalendar(
    props,
    state,
    ref
  );

  const monthDateFormatter = useDateFormatter({
    month: 'long',
    year: 'numeric',
    era:
      state.visibleRange.start.calendar.identifier === 'gregory' &&
      state.visibleRange.start.era === 'BC'
        ? 'short'
        : undefined,
    calendar: state.visibleRange.start.calendar.identifier,
    timeZone: state.timeZone,
  });

  return (
    <div
      ref={ref}
      className={clsx(styles['sapphire-calendar'], styleProps.className)}
      style={styleProps.style}
      {...mergeProps(calendarProps, filterDOMProps(props, { global: true }))}
    >
      <div className={styles['sapphire-calendar__tables-row']}>
        {[state.visibleRange.start]
          .concat(
            showTwoMonths ? state.visibleRange.start.add({ months: 1 }) : []
          )
          .map((startOfMonth, index, visibleMonths) => {
            const isLeft = visibleMonths.length > 1 && index === 0;
            const isRight =
              visibleMonths.length > 1 && index === visibleMonths.length - 1;

            return (
              <div
                key={index}
                className={clsx(styles['sapphire-calendar__table-container'], {
                  [styles['sapphire-calendar__table-container--md']]:
                    size === 'md',
                })}
              >
                <CalendarHeader
                  prevButtonProps={prevButtonProps}
                  nextButtonProps={nextButtonProps}
                  isLeft={isLeft}
                  isRight={isRight}
                >
                  {sentenceCase(
                    monthDateFormatter.format(
                      startOfMonth.toDate(state.timeZone)
                    )
                  )}
                </CalendarHeader>
                {visibleMonths.length === 1 ? (
                  <CalendarDaysGrid
                    startDate={startOfMonth}
                    state={state}
                    showWeekNumbers={showWeekNumbers}
                    showFixedWeeks={showFixedWeeks}
                  />
                ) : (
                  <CalendarDaysGrid
                    startDate={startOfMonth}
                    state={state}
                    showWeekNumbers={showWeekNumbers}
                    showFixedWeeks={showFixedWeeks}
                  />
                )}
              </div>
            );
          })}
      </div>
      {predefinedRanges && (
        <div className={styles['sapphire-calendar__custom-ranges']}>
          {(
            Object.entries(predefinedRanges) as Array<[P, PredefinedDateRange]>
          ).map(([key, { label, dateRange }]) => (
            <div key={key}>
              <Button
                variant="text"
                onPress={() => {
                  state.setValue(dateRange as RangeValue<T>, key);
                  state.setAnchorDate(null);
                  state.setFocusedDate(toCalendarDate(dateRange.end));
                  state.setFocused(true);
                }}
              >
                {label}
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Displays a calendar and allow users to select a consecutive range of dates.
 * One or two months can be visible at the same time.
 */
// eslint-disable-next-line
// @ts-ignore Union type too complex to represent (TODO: check if this is still
// an issue when we use TS@5.
export const RangeCalendar = React.forwardRef(_RangeCalendar) as <
  T extends DateValue,
  P extends string
>(
  props: RangeCalendarProps<T, P> & {
    ref?: React.ForwardedRef<HTMLDivElement>;
  }
) => ReturnType<typeof _RangeCalendar>;
